// stores/userStore.js
import { defineStore } from "pinia";
import { identifyUser } from "../helpers/analytics";
import axios from "axios";
import Cookies from "js-cookie";
import { extractDomain } from "../helpers";
import { useOrganizationStore } from "./organizationStore";
//const subscriptionStore = useSubscriptionStore();

export const useUserStore = defineStore("user", {
  state: () => ({
    user: Cookies.get("rmeUser429") || { loggedin: false },
    rmeSession: Cookies.get("rmeSession429") || null,
    error: "",
  }),
  getters: {},
  actions: {
    async loginWithEmail(email, pwd) {
      const result = await axios
        .post("/auth/login", {
          username: email.toLowerCase(),
          password: pwd,
        })
        .then((res) => {
          this.user = res.data.user;
          this.rmeSession = res.data.token;
          this.user.loggedin = true;
          identifyUser(this.user);
          lintrk("setUserData", { email: this.user.email });
          // Customer.io identification
          if (window._cio) {
            _cio.identify({
              id: this.user._id,
              email: this.user.email,
              name: this.user.name,
              created_at: this.user.created_at
                ? Math.floor(new Date(this.user.created_at).getTime() / 1000)
                : Math.floor(Date.now() / 1000)
            });
          }
          const domain = extractDomain(window.location.href);
          Cookies.set("rmeSession429", res.data.token, {
            domain: domain,
            expires: 30,
            sameSite: "Lax",
          });
          Cookies.set("rmeUser429", JSON.stringify(this.user), {
            expires: 30,
            domain: domain,
            sameSite: "Lax",
          });
          return true;
        })
        .catch((err) => {
          this.error = err?.response?.data?.message || err?.message;
          return false;
        });
      return result;
    },
    async register(
      email,
      name,
      pwd,
      checkoutPriceId = null,
      redirect = null,
      promoCode = null
    ) {
      try {
        // Read UTM params from sessionStorage (set on landing page visit) or URL
        const params = new URLSearchParams(window.location.search);
        const utmSource = sessionStorage.getItem('utm_source') || params.get('utm_source');
        const utmMedium = sessionStorage.getItem('utm_medium') || params.get('utm_medium');
        const utmCampaign = sessionStorage.getItem('utm_campaign') || params.get('utm_campaign');

        const response = await axios.post("/auth/register", {
          name: name,
          email: email,
          password: pwd,
          checkout_price_id: checkoutPriceId || undefined,
          promo_code: (checkoutPriceId && promoCode) || undefined,
          redirect: redirect || undefined,
          signup_source: utmSource ? 'paid' : 'direct',
          utm_source: utmSource || undefined,
          utm_medium: utmMedium || undefined,
          utm_campaign: utmCampaign || undefined,
        });

        // Join this browser's anonymous visits (referrer, UTMs, landing page) to
        // the new account now. The verification link often opens in a new
        // session or on another device, and by the time they sign in the
        // pre-signup history belongs to an anonymous id nothing points back to.
        // Same id as the login identify, never the email: PostHog cannot merge
        // two people who were both identified.
        if (response.data?.success && response.data.user?._id) {
          identifyUser(response.data.user);
        }

        return response.data;
      } catch (error) {
        console.error("Registration error:", error);
        return {
          success: false,
          message: error.response?.data?.message || "Registration failed. Please try again."
        };
      }
    },
    async refresh() {
      const organizationStore = useOrganizationStore();
      await axios
        .get("/api/user/me")
        .then((res) => {
          this.user = res.data.user;
          organizationStore.subdomain = res.data.user?.owner?.subdomain;
          this.user.loggedin = true;
          return true;
        })
        .catch((err) => {
          return false;
        });
    },
    async logout() {
      this.user = { loggedin: false };
      this.rmeSession = null;
      //delete local storage
      localStorage.clear();
      localStorage.removeItem("user");
      localStorage.removeItem("organization");
      //localStorage.removeItem("subscription");
      //delete cookies
      const domain = extractDomain(window.location.href);
      Cookies.remove("rmeUser429", {
        expires: 30,
        domain: domain,
        sameSite: "Lax",
      });
      Cookies.remove("rmeSession429", {
        expires: 30,
        domain: domain,
        sameSite: "Lax",
      });
      // Reset Customer.io on logout
      if (window._cio) {
        _cio.reset();
      }
      return true;
    },
    async updatePassword(newPassword, oldPassword) {
      const result = await axios.post("/api/user/update-password", {
        password: newPassword,
        oldPassword: oldPassword,
      });
      if (result) return result;
    },
    //update user name
    async updateName(name) {
      //get current user id
      const userId = this.user._id;
      const result = await axios.post(`/api/user/${userId}/name`, {
        name: name,
      });
      if (result) return result;
    },

    async updateEmailSentDuration(duration) {
      const userId = this.user._id;
      const result = await axios.post(
        `/api/user/${userId}/email-sent-duration`,
        {
          duration: duration,
        }
      );
      if (result) return result;
    },
    //get support url
    async getSupportUrl() {
      const result = await axios.get("/auth/hubspot");
      if (result) return result;
    },
    //upload user profile image to backend
    async uploadAvatar(formData) {
      try {
        // Sending the formData as-is, so Content-Type will be automatically set to 'multipart/form-data'
        const result = await axios.post("/api/user/avatar", formData, {
          headers: {
            Accept: "application/json", // Optional, to accept JSON response
          },
        });

        if (result) {
          return result.data; // Return the data from the response
          this.user.has_avatar = true;
        }
      } catch (error) {
        console.error("Error uploading avatar:", error);
        throw error; // You may want to handle this error in your calling function
      }
    },
  },
  persist: {
    afterRestore: (ctx) => {
      try {
        const data = JSON.parse(Cookies.get("rmeUser429"));
        if (data.email && !ctx.store.$state.user?.email) {
          ctx.store.$state.user = data;
        }
      } catch {
        //console.error("Error parsing user data");
      }
    },
  },
});
